import type { Collection } from "@tanstack/db";
import {
  createCollection,
  localOnlyCollectionOptions,
} from "@tanstack/react-db";

import type { DashboardUIRecord } from "./dashboardUITypes.js";
import {
  DASHBOARD_PERSISTENT_DEFAULTS,
  DASHBOARD_TRANSIENT_DEFAULTS,
} from "./dashboardUITypes.js";
import { persistedCollectionOptions } from "./persistence.js";
import type { PersistedCollectionPersistence } from "./persistence.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
export type DashboardUICol = Collection<
  DashboardUIRecord,
  string,
  any,
  any,
  any
>;
/* eslint-enable @typescript-eslint/no-explicit-any */

const ALL_DEFAULTS: DashboardUIRecord[] = [
  ...DASHBOARD_PERSISTENT_DEFAULTS,
  ...DASHBOARD_TRANSIENT_DEFAULTS,
];

export function createDashboardUICollection(
  persistence: PersistedCollectionPersistence | null,
): DashboardUICol {
  const transientDefaultsOnly = localOnlyCollectionOptions<
    DashboardUIRecord,
    string
  >({
    id: "dashboard-ui",
    getKey: (item) => item.key,
    // Persisted initialData is replayed after hydration, so seed only
    // transient rows here. Including layout defaults would overwrite saved
    // sidebar state on every app launch.
    initialData: DASHBOARD_TRANSIENT_DEFAULTS,
  });
  const opts = persistence
    ? persistedCollectionOptions({
        ...transientDefaultsOnly,
        persistence,
        schemaVersion: 1,
      })
    : localOnlyCollectionOptions<DashboardUIRecord, string>({
        id: "dashboard-ui",
        getKey: (item) => item.key,
        initialData: ALL_DEFAULTS,
      });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return createCollection(opts as any) as unknown as DashboardUICol;
}

/**
 * Module-level singleton for backward compatibility.
 * Consumers that import `dashboardUI` directly get an in-memory instance.
 * The persisted instance is created via createDashboardUICollection() in
 * CollectionsProvider.
 */
export const dashboardUI = createCollection(
  localOnlyCollectionOptions<DashboardUIRecord, string>({
    id: "dashboard-ui",
    getKey: (item) => item.key,
    initialData: ALL_DEFAULTS,
  }),
);

/**
 * Reset all transient dashboard UI state to defaults.
 *
 * Call this on sign-out so the module-level collection singleton
 * doesn't leak stale UI state (open modals, pending sessions, etc.)
 * into the next auth session.  On full page reloads this is handled
 * by getInitialData() above, but SPA-style logout/login keeps the
 * collection alive in memory.
 */
export function resetDashboardTransients(): void {
  for (const def of DASHBOARD_TRANSIENT_DEFAULTS) {
    dashboardUI.update(def.key, (draft) => {
      Object.assign(draft, def);
    });
  }
}

/**
 * @deprecated No longer needed -- defaults are seeded via initialData.
 * Kept as a no-op for call-site compatibility.
 */
export function ensureDashboardUIDefaults(): void {
  // Defaults are applied via initialData above.
}
